import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * One API read for a page: `loading` (first load or after the inputs changed), `ok`, `error`.
 * `reload({ quiet: true })` refetches while the current data stays on screen (live updates);
 * a newer load cancels the older one, so a slow response never overwrites a newer one.
 */
export type LoadState<T> =
  | { status: 'loading'; data?: undefined; error?: undefined }
  | { status: 'ok'; data: T; error?: undefined; refreshing: boolean }
  | { status: 'error'; data?: undefined; error: unknown };

export interface Loader<T> {
  state: LoadState<T>;
  reload: (opts?: { quiet?: boolean }) => void;
}

export function useLoad<T>(
  load: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
  enabled = true,
): Loader<T> {
  const [state, setState] = useState<LoadState<T>>({ status: 'loading' });
  const [tick, setTick] = useState<{ n: number; quiet: boolean }>({ n: 0, quiet: false });
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!enabled) return undefined;
    const controller = new AbortController();
    setState((prev) =>
      tick.quiet && prev.status === 'ok' ? { ...prev, refreshing: true } : { status: 'loading' },
    );
    loadRef.current(controller.signal).then(
      (data) => {
        if (!controller.signal.aborted) setState({ status: 'ok', data, refreshing: false });
      },
      (error: unknown) => {
        if (controller.signal.aborted) return;
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setState((prev) =>
          tick.quiet && prev.status === 'ok' ? { ...prev, refreshing: false } : { status: 'error', error },
        );
      },
    );
    return () => controller.abort();
    // `deps` are the caller's inputs; `load` is read from a ref so it is always the latest.
  }, [...deps, tick, enabled]);

  const reload = useCallback((opts?: { quiet?: boolean }) => {
    setTick((t) => ({ n: t.n + 1, quiet: opts?.quiet ?? false }));
  }, []);

  return { state, reload };
}

/**
 * Debounced callback: bursts of live events (qr_intent.updated + payment.received for one
 * payment) cause one refetch.
 */
export function useDebounced(fn: () => void, ms: number): () => void {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  return useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      fnRef.current();
    }, ms);
  }, [ms]);
}
